<script setup lang="ts">
import type { IChecklistItem, IValidationResult } from "@/interfaces";
import { usePlannerTemplateForm } from "@/stores/plannerTemplateFormStore";
import Button from "@/stories/buttons/Button/Button.vue";
import FormGroup from "@/stories/form/FormGroup/FormGroup.vue";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import FormContent from "@/views/planner/dialogs/templates/form/FormContent.vue";
import { ref } from "vue";
import joi from "joi";
import { validate } from "@/helpers/forms/validate";
import DeleteConfirmation from "@/stories/displays/DeleteConfirmation/DeleteConfirmation.vue";
import { nanoid } from "nanoid";
import ChecklistItem from "@/stories/displays/ChecklistItem/ChecklistItem.vue";
import SortableList from "@/stories/displays/SortableList/SortableList.vue";

const store = usePlannerTemplateForm();
const deleteItem = ref<IChecklistItem>();

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
});

const newItemText = ref<string>("");
const validation = ref<IValidationResult>();

const handleSubmit = (event: Event) => {
  event.preventDefault();

  validation.value = validate(VALIDATION_SCHEMA, { title: newItemText.value });
  if (!validation.value.isValid) {
    return;
  }

  store.form.checklist.push({
    key: nanoid(),
    title: newItemText.value,
  });
  newItemText.value = "";
};

const handleRemoveItemClick = (item: IChecklistItem) => {
  deleteItem.value = item;
};

const deleteSelectedItem = () => {
  if (!deleteItem.value) {
    return;
  }

  store.form.checklist = store.form.checklist.filter(
    (item) => item.key !== deleteItem.value?.key,
  );
  deleteItem.value = undefined;
};

const handleSortUpdate = (order: string[]) => {
  const itemMap = new Map(store.form.checklist.map((item) => [item.key, item]));
  const orderedKeys = new Set<string>();
  const orderedItems = order.flatMap((key) => {
    const item = itemMap.get(key);
    if (!item || orderedKeys.has(key)) return [];
    orderedKeys.add(key);
    return [item];
  });

  // Never drop rows: append anything missing from `order` instead of losing it
  const missingItems = store.form.checklist.filter((item) => {
    if (orderedKeys.has(item.key)) return false;
    orderedKeys.add(item.key);
    return true;
  });

  store.form.checklist = [...orderedItems, ...missingItems];
};
</script>

<template>
  <FormContent>
    <div class="h600 text-neutral1000 pb-6">
      {{ $t("global.checklist") }}
    </div>
    <FormGroup>
      <form
        class="flex justify-between items-center gap-2"
        @submit="handleSubmit"
      >
        <TextInput
          name="title"
          is-required
          :label="$t('templates.subtask_title')"
          :description="
            $t('global.validation_length', {
              rule: 50,
              current: 50 - (newItemText?.length || 0),
            })
          "
          :placeholder="$t('global.title')"
          :maxlength="50"
          :validation="validation"
          v-model="newItemText"
        >
          <template #input-right>
            <Button
              buttonType="submit"
              :text="$t('templates.add')"
              class="h-[3.5rem]"
            />
          </template>
        </TextInput>
      </form>

      <SortableList :items="store.form.checklist" @update="handleSortUpdate">
        <template #default="{ item }">
          <ChecklistItem
            :item="item as IChecklistItem"
            @delete="handleRemoveItemClick"
          />
        </template>
      </SortableList>
    </FormGroup>
  </FormContent>

  <DeleteConfirmation
    v-if="deleteItem"
    :title="$t('templates.checklists.delete_title')"
    deleteTextKey="global.deleteButton"
    @approve="deleteSelectedItem"
    @close="deleteItem = undefined"
  >
    {{ $t("templates.checklists.delete_description") }}
  </DeleteConfirmation>
</template>
